import React from 'react'
import type { Candidate, DetectedCard, ScanResponse } from '../types/card'

type Props = {
  response: ScanResponse
  photo: string
  ocrRaw?: string
  ocrCropDataUrl?: string
  onManualNumber?: (n: string) => void
}

const TIER_LABELS: Record<string, { color: string; bg: string; text: string }> = {
  high:   { color: 'text-emerald-300', bg: 'bg-emerald-500/15', text: 'high' },
  medium: { color: 'text-amber-300',   bg: 'bg-amber-500/15',   text: 'medium — verify' },
  low:    { color: 'text-red-300',     bg: 'bg-red-500/15',     text: 'low — no match' },
}

export default function ResultsList({ response, photo, ocrRaw, ocrCropDataUrl, onManualNumber }: Props) {
  const ocrFailed = response.ocr_card_numbers.length === 0
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-3">
        <img src={photo} alt="scan" className="w-full rounded-lg border border-edge" />
        <div className="mt-2 text-[10px] text-muted font-mono">
          decode {response.timings_ms.decode}ms · detect {response.timings_ms.detect}ms · match {response.timings_ms.match}ms · total {response.timings_ms.total}ms
        </div>

        {ocrFailed && onManualNumber && (
          <ManualNumberInput onSubmit={onManualNumber} />
        )}

        {ocrRaw !== undefined && (
          <div className="mt-2 text-[10px]">
            <div className="text-muted uppercase tracking-wider mb-1">OCR raw text</div>
            <pre className="p-2 rounded bg-ink/70 border border-edge font-mono text-emerald-200 whitespace-pre-wrap break-words max-h-24 overflow-y-auto">{ocrRaw || '(empty)'}</pre>
          </div>
        )}
        {ocrCropDataUrl && (
          <details className="mt-2 text-[10px]">
            <summary className="text-muted cursor-pointer">OCR crop preview</summary>
            <img
              src={ocrCropDataUrl}
              alt="OCR crop"
              className="mt-1 w-full max-h-64 object-contain rounded border border-edge bg-ink/70"
            />
          </details>
        )}
      </div>
      <div className="px-3 pb-6 space-y-4">
        {response.cards.map((card, i) => (
          <CardBlock key={i} card={card} index={i + 1} />
        ))}
      </div>
    </div>
  )
}

function ManualNumberInput({ onSubmit }: { onSubmit: (n: string) => void }) {
  const [value, setValue] = React.useState('')
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (value.trim()) onSubmit(value) }}
      className="mt-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5"
    >
      <div className="text-xs text-amber-200 mb-2">
        OCR didn't catch a card number — type it manually
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          placeholder="OP02-013"
          className="flex-1 px-3 py-2 rounded-md bg-ink text-white text-sm font-mono border border-edge focus:border-accent outline-none"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          type="submit"
          className="px-4 py-2 rounded-md bg-accent text-ink text-sm font-semibold"
        >
          narrow
        </button>
      </div>
    </form>
  )
}

function CardBlock({ card, index }: { card: DetectedCard; index: number }) {
  const tier = TIER_LABELS[card.tier]
  const winner = card.candidates.find((c) => c.cortex_id === card.winner_cortex_id) ?? card.candidates[0] ?? null

  return (
    <div className="bg-panel rounded-xl border border-edge overflow-hidden">
      <div className="p-3 flex gap-3 items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs text-muted">#{index}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${tier.bg} ${tier.color} uppercase tracking-wider`}>
              {tier.text}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-edge text-muted uppercase tracking-wider">
              {card.narrowed_by === 'ocr' ? 'OCR + pHash' : 'pHash only'}
            </span>
          </div>
          {winner ? (
            <>
              <div className="font-semibold leading-tight">{winner.name}</div>
              <div className="text-xs text-muted truncate">
                {winner.product_number} · {winner.set_name}
              </div>
              <div className="text-xs text-muted mt-1">
                pHash distance <span className="text-white font-mono">{winner.phash_distance}</span>
                <span className="mx-1">·</span>
                {card.detection.crop_width}×{card.detection.crop_height} crop
              </div>
            </>
          ) : (
            <div className="text-sm text-muted italic">no candidates</div>
          )}
        </div>
        {winner?.image_url && (
          <img
            src={winner.image_url}
            alt={winner.name}
            className="w-20 rounded bg-ink object-cover"
            style={{ aspectRatio: '5 / 7' }}
          />
        )}
      </div>

      <div className="border-t border-edge bg-ink/40 px-3 py-3">
        <div className="text-[10px] text-muted uppercase tracking-wider mb-2">
          {card.candidates.length} pHash candidates
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {card.candidates.map((c) => (
            <CandidateChip key={c.cortex_id} cand={c} isWinner={c.cortex_id === card.winner_cortex_id} />
          ))}
        </div>
      </div>
    </div>
  )
}

function CandidateChip({ cand, isWinner }: { cand: Candidate; isWinner: boolean }) {
  return (
    <div
      className={`shrink-0 w-24 rounded-md border ${isWinner ? 'border-accent ring-1 ring-accent' : 'border-edge'} bg-panel overflow-hidden`}
    >
      <div className="relative">
        {cand.image_url ? (
          <img
            src={cand.image_url}
            alt={cand.name}
            className="w-full block"
            style={{ aspectRatio: '5 / 7', objectFit: 'cover' }}
            loading="lazy"
          />
        ) : (
          <div className="w-full bg-ink" style={{ aspectRatio: '5 / 7' }} />
        )}
        {isWinner && (
          <div className="absolute top-1 left-1 bg-accent text-ink text-[9px] font-bold px-1.5 py-0.5 rounded">
            PICKED
          </div>
        )}
        <div className="absolute top-1 right-1 bg-ink/80 text-white text-[9px] px-1.5 py-0.5 rounded">
          #{cand.rank + 1}
        </div>
      </div>
      <div className="px-1.5 py-1 leading-tight">
        <div className="text-[10px] text-muted truncate">{cand.product_number ?? cand.cortex_id}</div>
        <div className="text-[10px] truncate" title={cand.name}>
          {cand.name}
        </div>
        <div className="text-[10px] text-muted font-mono mt-0.5">
          ph <span className="text-white">{cand.phash_distance}</span>
        </div>
      </div>
    </div>
  )
}
